import { useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent } from "react";
import {
    Box,
    Button,
    IconButton,
    Popover,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import {
    API_DATE_FORMAT,
    DISPLAY_DATE_FORMAT,
    displayDateToIso,
    isoDateToDisplay,
    normalizeDisplayDateSeparators,
} from "../../lib/api/b2bDates";

dayjs.extend(isoWeek);

const WEEKDAY_LABELS = ["пн", "вт", "ср", "чт", "пт", "сб", "нд"] as const;
const MONTH_LABELS = [
    "Януари", "Февруари", "Март", "Април", "Май", "Юни",
    "Юли", "Август", "Септември", "Октомври", "Ноември", "Декември",
] as const;

/** Тъмен календар — единен вид в целия CRM. */
const CALENDAR = {
    bg: "#212121",
    selected: "#4A7C44",
    selectedHover: "#3E6A3A",
    weekday: "#9E9E9E",
    outside: "#616161",
    dayHover: "#333333",
    clearBorder: "#FFFFFF",
    draftBg: "#E8E8E8",
    draftText: "#1A1A1A",
} as const;

type IsoDatePickerPopoverProps = {
    open: boolean;
    anchorEl: HTMLElement | null;
    value: string;
    onClose: () => void;
    onChange: (isoValue: string) => void;
};

function monthTitle(viewMonth: dayjs.Dayjs) {
    return `${MONTH_LABELS[viewMonth.month()]} ${viewMonth.year()}`;
}

function buildMonthDays(viewMonth: dayjs.Dayjs) {
    const start = viewMonth.startOf("month").startOf("isoWeek");
    const end = viewMonth.endOf("month").endOf("isoWeek");
    const days: dayjs.Dayjs[] = [];
    for (let day = start; day.isBefore(end) || day.isSame(end, "day"); day = day.add(1, "day")) {
        days.push(day);
    }
    return days;
}

export default function IsoDatePickerPopover({
    open,
    anchorEl,
    value,
    onClose,
    onChange,
}: IsoDatePickerPopoverProps) {
    const selected = dayjs(value, API_DATE_FORMAT, true);
    const [viewMonth, setViewMonth] = useState(() =>
        selected.isValid() ? selected.startOf("month") : dayjs().startOf("month"),
    );
    const [manualDraft, setManualDraft] = useState(() => isoDateToDisplay(value));

    useEffect(() => {
        if (!open) {
            return;
        }

        const parsed = dayjs(value, API_DATE_FORMAT, true);
        setViewMonth(parsed.isValid() ? parsed.startOf("month") : dayjs().startOf("month"));
        setManualDraft(isoDateToDisplay(value));
    }, [open, value]);

    const days = useMemo(() => buildMonthDays(viewMonth), [viewMonth]);

    function selectIso(iso: string) {
        onChange(iso);
        onClose();
    }

    function handleClose() {
        const iso = displayDateToIso(manualDraft);

        if (iso && iso !== value) {
            onChange(iso);
        }

        onClose();
    }

    function commitManualDraft() {
        const iso = displayDateToIso(manualDraft);

        if (!iso) {
            setManualDraft(isoDateToDisplay(value));
            return;
        }

        selectIso(iso);
    }

    function handleManualKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key !== "Enter") {
            return;
        }

        event.preventDefault();
        commitManualDraft();
    }

    function handleClear(event: MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        setManualDraft("");
        onChange("");
        onClose();
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
                paper: {
                    elevation: 8,
                    sx: {
                        mt: 0.75,
                        p: 1.75,
                        width: 300,
                        bgcolor: CALENDAR.bg,
                        color: "common.white",
                        borderRadius: "18px",
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Stack spacing={1.5}>
                <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
                    <IconButton
                        size="small"
                        aria-label="Предишен месец"
                        onClick={() => setViewMonth((current) => current.subtract(1, "month"))}
                        sx={{ color: "common.white" }}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: 0.2 }}
                    >
                        {monthTitle(viewMonth)}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="Следващ месец"
                        onClick={() => setViewMonth((current) => current.add(1, "month"))}
                        sx={{ color: "common.white" }}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, 1fr)",
                        gap: 0.35,
                        textAlign: "center",
                    }}
                >
                    {WEEKDAY_LABELS.map((label) => (
                        <Typography
                            key={label}
                            variant="caption"
                            sx={{ color: CALENDAR.weekday, py: 0.35, fontWeight: 600 }}
                        >
                            {label}
                        </Typography>
                    ))}

                    {days.map((day) => {
                        const iso = day.format(API_DATE_FORMAT);
                        const isSelected = selected.isValid() && day.isSame(selected, "day");
                        const isOutsideMonth = !day.isSame(viewMonth, "month");

                        return (
                            <Button
                                key={iso}
                                size="small"
                                onClick={() => selectIso(iso)}
                                sx={{
                                    minWidth: 34,
                                    width: 34,
                                    height: 34,
                                    mx: "auto",
                                    p: 0,
                                    borderRadius: "50%",
                                    fontSize: "0.8125rem",
                                    fontWeight: isSelected ? 700 : 600,
                                    lineHeight: 1,
                                    color: isOutsideMonth ? CALENDAR.outside : "common.white",
                                    bgcolor: isSelected ? CALENDAR.selected : "transparent",
                                    "&:hover": {
                                        bgcolor: isSelected ? CALENDAR.selectedHover : CALENDAR.dayHover,
                                    },
                                }}
                            >
                                {day.date()}
                            </Button>
                        );
                    })}
                </Box>

                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleClear}
                        sx={{
                            flexShrink: 0,
                            px: 1.75,
                            py: 0.6,
                            borderRadius: 999,
                            color: "common.white",
                            borderColor: CALENDAR.clearBorder,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": {
                                borderColor: CALENDAR.clearBorder,
                                bgcolor: CALENDAR.dayHover,
                            },
                        }}
                    >
                        Изчисти
                    </Button>
                    <TextField
                        size="small"
                        fullWidth
                        value={manualDraft}
                        placeholder={DISPLAY_DATE_FORMAT}
                        onChange={(event) => setManualDraft(normalizeDisplayDateSeparators(event.target.value))}
                        onBlur={commitManualDraft}
                        onKeyDown={handleManualKeyDown}
                        slotProps={{
                            htmlInput: {
                                sx: {
                                    color: CALENDAR.draftText,
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                    textAlign: "center",
                                    "&::placeholder": {
                                        color: "#757575",
                                        opacity: 1,
                                    },
                                },
                            },
                            input: {
                                sx: {
                                    bgcolor: CALENDAR.draftBg,
                                    color: CALENDAR.draftText,
                                    borderRadius: "10px",
                                    py: 0.65,
                                    "& fieldset": { border: "none" },
                                },
                            },
                        }}
                    />
                </Stack>
            </Stack>
        </Popover>
    );
}
